import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './MainLayout.scss';
import { Bookmark, NavigationData, Report } from 'core/slices/models';
import { acquireAccessToken } from 'core/services/ApiService';
import { msalInstance } from '../index';
import {
  activeBookmarkChanged,
  activeReportChanged,
  clearActiveBookmark,
} from 'core/slices/reports';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'core/store';
import { decodeB64 } from './functions/reportUtils';
import { findReport } from '../domain/functions/findReport';
import { getActiveReport } from './functions/getActiveReport';
import { TopNavbar } from './top-navbar/TopNavbar';
import { MainContent } from './main-content/MainContent';
import { SidebarMenu } from './sidebar-menu/SidebarMenu';
import { Popup } from './Popup';

const MainLayout: React.FC = () => {
  // Ref to track previous reportId for navigation logic
  const prevReportIdRef = useRef<string | undefined>(undefined);
  const [showSidebar, setShowSidebar] = useState<boolean>(true);
  const toggleSidebar = () => setShowSidebar(s => !s);

  const [navData, setNavData] = useState<NavigationData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);
  const [activeReport, setActiveReport] = useState<Report | undefined>(
    undefined
  );
  const [activeBookmark, setActiveBookmark] = useState<Bookmark | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    mounted.current = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);

        const token = await acquireAccessToken(msalInstance);

        const res = await fetch(`/api/v2/projects/fmsr/reports`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            // For GET requests, you usually don't need Content-Type unless sending a body.
            // Remove it to avoid confusing some servers/proxies.
          } as HeadersInit,
        });

        if (!res.ok) {
          throw new Error(
            `Failed to fetch navigation data: ${res.status} ${res.statusText}`
          );
        }

        const data: NavigationData = await res.json();

        if (!data || Object.keys(data).length === 0) {
          setError('No data');
        }

        if (!data.children || !data.children.length) {
          setError('No Reports available');
        }

        if (mounted.current) {
          setNavData(data);
        }
      } catch (e: any) {
        if (mounted.current) {
          setError(e?.message ?? 'Unknown error loading navigation data');
        }
      } finally {
        if (mounted.current) setLoading(false);
      }
    })();

    return () => {
      mounted.current = false;
    };
  }, []);

  /**
   * When the URL path changes, update the active report based on the path.
   */
  useEffect(() => {
    if (navData) {
      const activeReport = getActiveReport({
        navData,
        pathname: location.pathname,
        defaultId: 'home_page',
      });
      if (activeReport) {
        setActiveReport(activeReport);
        dispatch(activeReportChanged(activeReport));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navData]);

  useEffect(() => {
    if (activeReport) {
      // Preserve existing query params when navigating to grid report
      if (activeReport.tableView) {
        let queryString = '';
        if (activeBookmark) {
          queryString = decodeB64(activeBookmark.reportState);
        }
        const path = `/reports/grid/${activeReport.id}`;
        // Get current query params from location
        const params = new URLSearchParams(location.search);
        // Reset filter ONLY if report changes (not on initial entry)
        const prevReportId = prevReportIdRef.current;
        if (prevReportId && prevReportId !== activeReport.id) {
          params.delete('filter');
        }
        prevReportIdRef.current = activeReport.id;
        const newSearch = params.toString();
        // If there is a bookmark, override queryString, otherwise keep current query params (with filter reset if report changed)
        if (activeBookmark && queryString) {
          navigate(`${path}?${queryString}`);
        } else {
          navigate(newSearch ? `${path}?${newSearch}` : path);
        }
      } else if (activeReport.pbiView?.id) {
        const query = new URLSearchParams();
        if (activeBookmark) {
          query.set('bookmarkState', activeBookmark.reportState);
        }
        navigate(`/reports/pbi/${activeReport.pbiView.id}?${query.toString()}`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeReport, activeBookmark]);

  // when report is selected in the nav menu
  const handleReportClick = useCallback(
    (report: Report) => {
      setActiveReport(report);
      //clear active bookmark
      setActiveBookmark(null);
      //TODO is this still needed here?
      dispatch(activeReportChanged(report));
      //a report is changed, clear active bookmark
      dispatch(clearActiveBookmark());
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeReport?.id, dispatch, navigate]
  );

  const handleBookmarkClick = useCallback(
    (bookmark: Bookmark) => {
      const report = findReport(navData, bookmark.reportId);
      if (report) {
        setActiveReport(report);
        setActiveBookmark(bookmark);
        dispatch(activeReportChanged(report));
        dispatch(activeBookmarkChanged(bookmark));
      }
    },
    [navData, dispatch]
  );

  return (
    <>
      <div className={'main-layout-body'}>
        <TopNavbar showSidebar={showSidebar} toggleSidebar={toggleSidebar} />

        <div className="main-container-wrapper">
          <SidebarMenu
            showSidebar={showSidebar}
            navData={navData}
            handleReportClick={handleReportClick}
            handleBookmarkClick={handleBookmarkClick}
            activeReport={activeReport}
          />
          <MainContent loading={loading} error={error} navData={navData} />
        </div>
      </div>
    </>
  );
};

export default MainLayout;
